import React, { useEffect, useState } from 'react';
import qrcode from 'qrcode-generator';
import ThemeToggle from '../components/ThemeToggle';
import { authClient, AuthError, decodeJwt, IssuedToken, LoginResult, otpSecret, SignedIn } from './authClient';
import './PlatformAuth.css';

// Sign-in screens for the auth plane. /set-password stands on its own (it arrives
// from an emailed link); signing in happens in the console (ConsoleSignIn).

export const PLATFORM_PATHS = ['/set-password'];

export function isPlatformPath(pathname: string): boolean {
  return PLATFORM_PATHS.some((p) => pathname === p || pathname === p + '/');
}

function message(err: unknown): string {
  return err instanceof AuthError || err instanceof Error ? err.message : 'Something went wrong.';
}

export const Shell: React.FC<{ title: string; intro?: React.ReactNode; children: React.ReactNode }> = ({ title, intro, children }) => (
  <div className="app app-setup">
    <header className="app-header">
      <div className="brand-lockup">
        <img src="/form-pipeline-icon.png" alt="" className="brand-mark" aria-hidden="true" />
        <div className="brand-text">
          <h1 className="brand-name">Form Pipeline</h1>
          <span className="brand-eyebrow">Sign in</span>
        </div>
      </div>
      <div className="topbar-actions setup-topbar-actions">
        <ThemeToggle />
      </div>
    </header>
    <main className="setup-page">
      <section className="setup-card">
        <div className="setup-card-header">
          <h2>{title}</h2>
          {intro && <p>{intro}</p>}
        </div>
        {children}
      </section>
    </main>
  </div>
);

const ErrorAlert: React.FC<{ error: string | null }> = ({ error }) =>
  error ? (
    <div className="alert alert-error" role="alert">
      <span>Error</span>
      <span>{error}</span>
    </div>
  ) : null;

// --- /set-password -----------------------------------------------------------

export const SetPassword: React.FC = () => {
  // The token arrives in the URL fragment, which browsers never send to servers.
  // Read it once, then drop it from the address bar and history.
  const [token] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') || '');
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Shell title="Link not valid" intro="This set-password link is incomplete. Ask an administrator for a new one.">
        <a className="btn btn-primary setup-submit" href="/">Go to sign in</a>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="Password set" intro="You can sign in now.">
        <a className="btn btn-primary setup-submit" href="/">Sign in</a>
      </Shell>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('The passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await authClient.setPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Choose a password" intro="At least 12 characters. A short sentence works well.">
      <form onSubmit={submit}>
        <ErrorAlert error={error} />
        <div className="form-group">
          <label htmlFor="new-password">New password</label>
          <input id="new-password" type="password" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} disabled={busy} autoFocus required minLength={12} />
        </div>
        <div className="form-group">
          <label htmlFor="confirm-password">Confirm password</label>
          <input id="confirm-password" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} disabled={busy} required />
        </div>
        <button className="btn btn-primary setup-submit" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
      </form>
    </Shell>
  );
};

// --- /signin -------------------------------------------------------------------

type Stage =
  | { kind: 'credentials' }
  | { kind: 'mfa'; challenge: string; enrolUri?: string }
  | { kind: 'recovery-codes'; signedIn: SignedIn }
  | { kind: 'signed-in'; signedIn: SignedIn };

/**
 * The sign-in flow. With onSignedIn, it hands over once signed in (the console);
 * without it, it shows the standalone signed-in page.
 */
export const SignIn: React.FC<{ onSignedIn?: (s: SignedIn) => void; footer?: React.ReactNode }> = ({ onSignedIn, footer }) => {
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  useEffect(() => {
    if (stage.kind === 'signed-in' && onSignedIn) {
      onSignedIn(stage.signedIn);
    }
  }, [stage, onSignedIn]);

  switch (stage.kind) {
    case 'credentials':
      return <Credentials onResult={(r) => setStage(next(r))} footer={footer} />;
    case 'mfa':
      return (
        <MfaStep
          challenge={stage.challenge}
          enrolUri={stage.enrolUri}
          onSignedIn={(s) => setStage(s.recoveryCodes?.length ? { kind: 'recovery-codes', signedIn: s } : { kind: 'signed-in', signedIn: s })}
          onRestart={() => setStage({ kind: 'credentials' })}
        />
      );
    case 'recovery-codes':
      return <RecoveryCodes codes={stage.signedIn.recoveryCodes || []} onDone={() => setStage({ kind: 'signed-in', signedIn: stage.signedIn })} />;
    case 'signed-in':
      return onSignedIn ? <Shell title="Signing in…">{null}</Shell> : <SignedInView signedIn={stage.signedIn} onSignedOut={() => setStage({ kind: 'credentials' })} />;
  }
};

function next(result: LoginResult): Stage {
  if (result.status === 'mfa_enrollment_required') {
    return { kind: 'mfa', challenge: result.challenge, enrolUri: result.otpauthUri };
  }
  if (result.status === 'mfa_required') {
    return { kind: 'mfa', challenge: result.challenge };
  }
  return { kind: 'signed-in', signedIn: result };
}

const Credentials: React.FC<{ onResult: (r: LoginResult) => void; footer?: React.ReactNode }> = ({ onResult, footer }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      onResult(await authClient.login(email.trim(), password));
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };

  return (
    <Shell title="Sign in">
      <form onSubmit={submit}>
        <ErrorAlert error={error} />
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input id="email" type="email" autoComplete="username" value={email}
            onChange={(e) => setEmail(e.target.value)} disabled={busy} autoFocus required />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input id="password" type="password" autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} disabled={busy} required />
        </div>
        <button className="btn btn-primary setup-submit" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {footer}
    </Shell>
  );
};

const MfaStep: React.FC<{
  challenge: string;
  enrolUri?: string;
  onSignedIn: (s: SignedIn) => void;
  onRestart: () => void;
}> = ({ challenge, enrolUri, onSignedIn, onRestart }) => {
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const secret = enrolUri ? otpSecret(enrolUri) : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const value = code.trim();
      onSignedIn(await authClient.verifyMfa(challenge, useRecovery ? { recoveryCode: value } : { code: value.replace(/\s+/g, '') }));
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };

  return (
    <Shell
      title={enrolUri ? 'Set up two-step sign-in' : 'Two-step sign-in'}
      intro={enrolUri ? 'Your account needs an authenticator app (1Password, Google Authenticator, Authy…).' : 'Enter the 6-digit code from your authenticator app.'}
    >
      {enrolUri && (
        <ol className="pa-steps">
          <li className="pa-qr-step">
            Scan this with your authenticator app:
            <QrCode value={enrolUri} />
          </li>
          <li>
            <a className="btn btn-secondary" href={enrolUri}>Add to authenticator app</a>
            <small> Opens your authenticator on this device, if it supports it.</small>
          </li>
          {secret && (
            <li>
              Or add it by hand with this setup key:
              <code className="pa-secret" aria-label="Setup key">{secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              <CopyButton text={secret} label="Copy key" />
            </li>
          )}
          <li>Then enter the 6-digit code it shows.</li>
        </ol>
      )}
      <form onSubmit={submit}>
        <ErrorAlert error={error} />
        <div className="form-group">
          <label htmlFor="mfa-code">{useRecovery ? 'Recovery code' : '6-digit code'}</label>
          <input id="mfa-code" value={code} onChange={(e) => setCode(e.target.value)} disabled={busy} autoFocus required
            {...(useRecovery ? { autoComplete: 'off' } : { inputMode: 'numeric' as const, autoComplete: 'one-time-code', pattern: '[0-9 ]{6,7}' })} />
        </div>
        <button className="btn btn-primary setup-submit" type="submit" disabled={busy}>
          {busy ? 'Checking…' : 'Verify'}
        </button>
      </form>
      <div className="pa-links">
        {!enrolUri && (
          <button type="button" className="btn btn-ghost" onClick={() => { setUseRecovery(!useRecovery); setCode(''); }}>
            {useRecovery ? 'Use a 6-digit code' : 'Use a recovery code'}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={onRestart}>Start over</button>
      </div>
    </Shell>
  );
};

/** Copies text to the clipboard; the label flips to "Copied" briefly. */
const CopyButton: React.FC<{ text: string; label: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the key text itself is selectable (user-select: all).
    }
  };
  return (
    <button type="button" className="btn btn-secondary pa-copy" onClick={copy}>
      {copied ? 'Copied' : label}
    </button>
  );
};

/**
 * QR code for scanning from another device. Hidden on touch-only screens by CSS,
 * since a phone can't scan its own screen.
 */
const QrCode: React.FC<{ value: string }> = ({ value }) => {
  const qr = qrcode(0, 'M');
  qr.addData(value);
  qr.make();
  const n = qr.getModuleCount();
  let path = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) {
        path += `M${c + 2} ${r + 2}h1v1h-1z`;
      }
    }
  }
  return (
    <svg className="pa-qr" viewBox={`0 0 ${n + 4} ${n + 4}`} role="img" aria-label="QR code for your authenticator app" shapeRendering="crispEdges">
      <rect width={n + 4} height={n + 4} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
};

const RecoveryCodes: React.FC<{ codes: string[]; onDone: () => void }> = ({ codes, onDone }) => (
  <Shell title="Save your recovery codes" intro="Each code works once if you lose your authenticator. They will not be shown again.">
    <ul className="pa-codes">
      {codes.map((c) => <li key={c}><code>{c}</code></li>)}
    </ul>
    <CopyButton text={codes.join('\n')} label="Copy all codes" />
    <button className="btn btn-primary setup-submit" type="button" onClick={onDone}>I've saved them</button>
  </Shell>
);

const SignedInView: React.FC<{ signedIn: SignedIn; onSignedOut: () => void }> = ({ signedIn, onSignedOut }) => {
  const [issued, setIssued] = useState<IssuedToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const claims = issued ? decodeJwt(issued.accessToken) : null;
  const header = issued ? decodeJwt(issued.accessToken, 0) : null;

  const getToken = async (accountId: string) => {
    setError(null);
    setBusy(true);
    try {
      setIssued(await authClient.token(accountId));
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    try {
      await authClient.logout();
    } finally {
      onSignedOut();
    }
  };

  return (
    <Shell title={`Signed in as ${signedIn.person.name}`} intro={signedIn.person.email}>
      <ErrorAlert error={error} />
      <h3 className="pa-subhead">Accounts</h3>
      {signedIn.memberships.length === 0 ? (
        <p className="pa-muted">You are not a member of any account yet.</p>
      ) : (
        <ul className="pa-accounts">
          {signedIn.memberships.map((m) => (
            <li key={m.accountId}>
              <span>{m.name} <small className="pa-muted">({m.slug}{m.status !== 'active' ? `, ${m.status}` : ''})</small></span>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => getToken(m.accountId)}>Get access token</button>
            </li>
          ))}
        </ul>
      )}
      {issued && (
        <div className="pa-token" aria-label="Access token">
          <h3 className="pa-subhead">Access token for {issued.account.name}</h3>
          <dl>
            <dt>Data plane</dt><dd>{issued.apiBaseUrl}</dd>
            <dt>Expires in</dt><dd>{Math.round(issued.expiresIn / 60)} minutes</dd>
            {claims && (
              <>
                <dt>Role</dt><dd>{String(claims.role)}</dd>
                <dt>Permissions</dt><dd>{Array.isArray(claims.perms) && claims.perms.length ? (claims.perms as string[]).join(', ') : 'none'}</dd>
                <dt>Signing key</dt><dd>{String(header?.kid ?? '?')}</dd>
              </>
            )}
          </dl>
        </div>
      )}
      <button className="btn btn-secondary setup-submit" type="button" onClick={signOut}>Sign out</button>
    </Shell>
  );
};

const PlatformAuth: React.FC = () => <SetPassword />;

export default PlatformAuth;
